import { GridSortModel } from '@mui/x-data-grid-premium';
import {
  DateRangeValue, DatePresetKey, isDatePresetKey, resolveDatePreset, getPresetRanges,
} from './date-presets';
import { isLevelValue } from '../constants/tracking-groupings';
import { SORTABLE_FIELDS } from './user-settings';
import type { FilterEntry } from '../pages/tracking/tracking-page.format';
import { fmtDate } from '../pages/tracking/tracking-page.format';

/** Partial override: applies only the fields present. */
export interface TrackingPreset {
  id: string;
  name: string;
  /** Relative key, not dates — stays relative over time. */
  date_preset?: DatePresetKey;
  group_by?: string;
  drilldowns?: string[];
  /** API column name ('name' = the group column). */
  sort?: { field: string; order: 'asc' | 'desc' };
  filters?: FilterEntry[];
  tags?: string[];
}

export type PresetField = 'date' | 'group' | 'drilldown' | 'filters' | 'tags' | 'sort';
export const PRESET_FIELDS: { key: PresetField; label: string }[] = [
  { key: 'date', label: 'Date range' },
  { key: 'group', label: 'Group by' },
  { key: 'drilldown', label: 'Drilldown' },
  { key: 'filters', label: 'Filters' },
  { key: 'tags', label: 'Tags' },
  { key: 'sort', label: 'Sort' },
];

export const DEFAULT_PRESETS: TrackingPreset[] = [
  { id: 'seed-traffic-source', name: 'Traffic Sources', group_by: 'traffic_source_id' },
  { id: 'seed-flow', name: 'Flows', group_by: 'flow_id' },
  { id: 'seed-network', name: 'Networks', group_by: 'network_id' },
  { id: 'seed-landing-page', name: 'Landing Pages', group_by: 'landing_page_id' },
  { id: 'seed-offer', name: 'Offers', group_by: 'offer_id' },
  { id: 'seed-region', name: 'State / Region', group_by: 'region' },
];

const isSortField = (v: unknown): v is string => v === 'name' || SORTABLE_FIELDS.some((f) => f.value === v);

// prototype keys crash useFilterParams's keyed accumulator
const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

function isFilterEntry(v: unknown): v is FilterEntry {
  const f = v as FilterEntry;
  return typeof v === 'object' && v !== null
    && typeof f.field === 'string' && !UNSAFE_KEYS.has(f.field)
    && typeof f.value === 'string'
    && typeof f.label === 'string';
}

function resolveOne(raw: unknown): TrackingPreset | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || !r.id || typeof r.name !== 'string' || !r.name) return null;

  const out: TrackingPreset = { id: r.id, name: r.name };
  if (isDatePresetKey(r.date_preset)) out.date_preset = r.date_preset;
  if (isLevelValue(r.group_by)) out.group_by = r.group_by;
  if (Array.isArray(r.drilldowns)) out.drilldowns = r.drilldowns.filter(isLevelValue);
  if (Array.isArray(r.filters)) out.filters = r.filters.filter(isFilterEntry);
  if (Array.isArray(r.tags)) out.tags = r.tags.filter((t): t is string => typeof t === 'string');
  if (isSortField((r.sort as { field?: unknown })?.field)
    && ((r.sort as { order?: unknown }).order === 'asc' || (r.sort as { order?: unknown }).order === 'desc')) {
    out.sort = (r.sort as TrackingPreset['sort']);
  }
  return out;
}

export function resolveTrackingPresets(raw: unknown): TrackingPreset[] {
  if (!Array.isArray(raw)) return DEFAULT_PRESETS;
  const resolved = raw.map(resolveOne).filter((p): p is TrackingPreset => p !== null);
  return resolved.length ? resolved : DEFAULT_PRESETS;
}

export interface PresetApplyTarget {
  setDateRange: (v: DateRangeValue) => void;
  setGroupBy: (v: string) => void;
  setDrilldownChain: (v: string[]) => void;
  setFilters: (v: FilterEntry[]) => void;
  setTagIds: (v: string[]) => void;
  setSortModel: (v: GridSortModel) => void;
}

/** grid: display_name; API/preset: name */
const toGridField = (field: string) => (field === 'name' ? 'display_name' : field);

export function applyPreset(preset: TrackingPreset, t: PresetApplyTarget): void {
  if (preset.date_preset) t.setDateRange(resolveDatePreset(preset.date_preset));
  if (preset.group_by !== undefined) t.setGroupBy(preset.group_by);
  // !== undefined: an empty list is a real "clear", not a skip
  if (preset.drilldowns !== undefined) t.setDrilldownChain(preset.drilldowns);
  if (preset.filters !== undefined) t.setFilters(preset.filters);
  if (preset.tags !== undefined) t.setTagIds(preset.tags);
  if (preset.sort) t.setSortModel([{ field: toGridField(preset.sort.field), sort: preset.sort.order }]);
}

export interface PresetMatchState {
  dateRange: DateRangeValue;
  groupBy: string;
  drilldownChain: string[];
  filters: FilterEntry[];
  tagIds: string[];
  sortBy: string;
  sortOrder: 'asc' | 'desc';
}

const sameRange = (a: DateRangeValue, b: DateRangeValue) => {
  const end = (d: Date | null) => (d ? fmtDate(d) : '');
  return end(a[0]) === end(b[0]) && end(a[1]) === end(b[1]);
};
const sameSet = (a: string[], b: string[]) => a.length === b.length && [...a].sort().join() === [...b].sort().join();
const sameFilters = (a: FilterEntry[], b: FilterEntry[]) => sameSet(
  a.map((f) => `${f.field}:${f.value}`), b.map((f) => `${f.field}:${f.value}`),
);

/** Date matched by resolved range, not the stored key. */
export function isPresetActive(preset: TrackingPreset, s: PresetMatchState): boolean {
  if (preset.date_preset && !sameRange(resolveDatePreset(preset.date_preset), s.dateRange)) return false;
  if (preset.group_by !== undefined && preset.group_by !== s.groupBy) return false;
  if (preset.drilldowns !== undefined
    && preset.drilldowns.join() !== s.drilldownChain.join()) return false;
  if (preset.filters !== undefined && !sameFilters(preset.filters, s.filters)) return false;
  if (preset.tags !== undefined && !sameSet(preset.tags, s.tagIds)) return false;
  if (preset.sort && (preset.sort.field !== s.sortBy || preset.sort.order !== s.sortOrder)) return false;
  return Boolean(preset.date_preset || preset.group_by !== undefined || preset.drilldowns !== undefined
    || preset.filters !== undefined || preset.tags !== undefined || preset.sort);
}

export function matchingDatePreset(dateRange: DateRangeValue): DatePresetKey | undefined {
  return getPresetRanges().find((p) => sameRange(p.dateRange, dateRange))?.key;
}

export function capturePreset(
  id: string, name: string, fields: Set<PresetField>, s: PresetMatchState, datePreset: DatePresetKey,
): TrackingPreset {
  const out: TrackingPreset = { id, name };
  if (fields.has('date')) out.date_preset = datePreset;
  if (fields.has('group')) out.group_by = s.groupBy;
  if (fields.has('drilldown')) out.drilldowns = s.drilldownChain;
  if (fields.has('filters')) out.filters = s.filters;
  if (fields.has('tags')) out.tags = s.tagIds;
  if (fields.has('sort')) out.sort = { field: s.sortBy, order: s.sortOrder };
  return out;
}
